import { api } from "./api";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.ready;
}

export async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("SW register failed", e);
    return null;
  }
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeForPush(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const { public_key } = await api.get<{ public_key: string | null }>(
    "/agent/push/public-key",
  );
  if (!public_key) throw new Error("Push ist nicht konfiguriert (kein VAPID-Key)");

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Du hast Benachrichtigungen abgelehnt");

  const reg = await registerSW();
  if (!reg) throw new Error("Service-Worker konnte nicht geladen werden");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const keyBytes = urlBase64ToUint8Array(public_key);
    // TS-Strict will Uint8Array<ArrayBuffer> — wir kopieren in fresh buffer.
    const fresh = new Uint8Array(keyBytes.length);
    fresh.set(keyBytes);
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: fresh,
    });
  }

  await api.post("/agent/push/subscribe", {
    endpoint: sub.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(sub.getKey("p256dh")),
      auth: arrayBufferToBase64(sub.getKey("auth")),
    },
  });
  return sub;
}

export async function unsubscribeFromPush(): Promise<void> {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  await api.post("/agent/push/unsubscribe", { endpoint: sub.endpoint });
  await sub.unsubscribe();
}

export const sendPushTest = () => api.post<{ sent: number }>("/agent/push/test");
